/**
 * Verification Banner Component
 * Displays access limits, verification status, and upgrade prompts
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
    FiShield,
    FiCheckCircle,
    FiAlertCircle,
    FiArrowRight,
    FiTrendingUp,
    FiUsers,
    FiStar
} from 'react-icons/fi';
import { User } from '@/types/auth';
import { AccessLimitStatus } from '@/services/accessLimitService';
import { useGetPlatformStatsQuery } from '@/services/statsApi';

interface VerificationBannerProps {
    user: User;
    accessLimit?: AccessLimitStatus;
    type: 'gig' | 'application' | 'general';
    variant?: 'compact' | 'full' | 'inline';
    className?: string;
    onVerificationClick?: () => void;
    verificationStatus?: 'verified' | 'unverified' | 'pending' | 'under_review'; // Optional override from API
}

const formatTierLabel = (tier: string) =>
    tier.charAt(0).toUpperCase() + tier.slice(1).replace(/_/g, ' ')

const VerificationBanner: React.FC<VerificationBannerProps> = ({
    user,
    accessLimit,
    type,
    variant = 'full',
    className = '',
    onVerificationClick,
    verificationStatus
}) => {
    // Fetch platform stats for dynamic data
    const { data: platformStats } = useGetPlatformStatsQuery();

    // Use API verification status if provided, otherwise check user object
    const isVerified = verificationStatus === 'verified' ||
        user.profile?.verified ||
        user.identityVerification?.status === 'verified';

    // Don't show banner if user is already verified
    if (type !== 'general' && isVerified && !accessLimit) {
        return null;
    }

    // Don't show if they have unlimited access
    if (type !== 'general' && accessLimit?.isUnlimited && isVerified) {
        return null;
    }

    const getBenefitsList = () => {
        const benefits = user.role === 'employer'
            ? [
                'Unlimited gig posting',
                'Advanced applicant filtering',
                'Priority listing placement',
                'Dedicated support channel'
            ]
            : [
                'Unlimited job applications',
                'Portfolio showcase features',
                'Advanced search filters',
                'Direct employer messaging'
            ];
        return benefits;
    };

    const getStatusConfig = () => {
        if (accessLimit && !accessLimit.hasAccess) {
            return {
                variant: 'danger',
                icon: FiAlertCircle,
                title: `${type === 'gig' ? 'Gig Posting' : 'Application'} Limit Reached`,
                message: accessLimit.upgradeMessage || accessLimit.message || `You've reached your ${type} limit`,
                urgency: 'high'
            };
        }

        if (accessLimit && typeof accessLimit.remaining === 'number' && accessLimit.remaining <= 1) {
            return {
                variant: 'warning',
                icon: FiAlertCircle,
                title: `Almost at Your ${type === 'gig' ? 'Gig Posting' : 'Application'} Limit`,
                message: accessLimit.upgradeMessage || accessLimit.message || `${accessLimit.remaining} remaining`,
                urgency: 'medium'
            };
        }

        return {
            variant: 'info',
            icon: FiShield,
            title: 'Unlock Premium Features',
            message: 'Upgrade your plan to unlock higher monthly limits and benefits',
            urgency: 'low'
        };
    };

    const statusConfig = getStatusConfig();

    if (variant === 'compact') {
        return (
            <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 ${className}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <statusConfig.icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        <div>
                            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                                {accessLimit?.message || 'Upgrade for higher monthly limits'}
                            </p>
                        </div>
                    </div>
                    <Link
                        to="/subscription"
                        onClick={onVerificationClick}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
                    >
                        Manage Plan
                        <FiArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                </div>
            </div>
        );
    }

    if (variant === 'inline') {
        return (
            <div className={`bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 p-4 ${className}`}>
                <div className="flex items-start">
                    <FiAlertCircle className="h-5 w-5 text-yellow-400 mt-0.5 mr-3" />
                    <div className="flex-1">
                        <p className="text-sm text-yellow-700 dark:text-yellow-200">
                            {accessLimit?.upgradeMessage || 'Upgrade your plan for higher monthly limits'}
                        </p>
                    </div>
                    <Link
                        to="/subscription"
                        onClick={onVerificationClick}
                        className="text-yellow-700 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 text-sm font-medium underline ml-4"
                    >
                        Upgrade Now
                    </Link>
                </div>
            </div>
        );
    }

    // Full variant
    const variantClasses = {
        info: 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/40 border-blue-200 dark:border-blue-800',
        warning: 'bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-900/40 border-yellow-200 dark:border-yellow-800',
        danger: 'bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/40 border-red-200 dark:border-red-800'
    };

    const textClasses = {
        info: 'text-blue-900 dark:text-blue-100',
        warning: 'text-yellow-900 dark:text-yellow-100',
        danger: 'text-red-900 dark:text-red-100'
    };

    const iconClasses = {
        info: 'text-blue-600 dark:text-blue-400',
        warning: 'text-yellow-600 dark:text-yellow-400',
        danger: 'text-red-600 dark:text-red-400'
    };

    const buttonClasses = {
        info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        warning: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
        danger: 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
    };

    return (
        <div className={`${variantClasses[statusConfig.variant as keyof typeof variantClasses]} border rounded-lg p-4 sm:p-6 ${className}`}>
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-4">
                <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                    <div className={`flex-shrink-0 rounded-full p-2 bg-white dark:bg-gray-800 shadow-sm`}>
                        <statusConfig.icon className={`h-5 w-5 sm:h-6 sm:w-6 ${iconClasses[statusConfig.variant as keyof typeof iconClasses]}`} />
                    </div>

                    <div className="flex-1 min-w-0">
                        <h3 className={`text-base sm:text-lg font-semibold ${textClasses[statusConfig.variant as keyof typeof textClasses]} mb-1 sm:mb-2`}>
                            {statusConfig.title}
                        </h3>

                        <p className={`text-xs sm:text-sm ${textClasses[statusConfig.variant as keyof typeof textClasses]} opacity-80 mb-3 sm:mb-4`}>
                            {statusConfig.message}
                        </p>

                        {/* Access Limit Status */}
                        {accessLimit && (
                            <div className="mb-3 sm:mb-4">
                                <div className={`inline-flex items-center px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-medium bg-white dark:bg-gray-800 ${textClasses[statusConfig.variant as keyof typeof textClasses]} border`}>
                                    {accessLimit.isUnlimited ? (
                                        <>
                                            <FiCheckCircle className="h-3 w-3 mr-1" />
                                            Unlimited via {formatTierLabel(accessLimit.subscriptionTier || 'current')}
                                        </>
                                    ) : (
                                        <>
                                            <FiAlertCircle className="h-3 w-3 mr-1" />
                                            <span className="font-semibold">{Math.max(accessLimit.remaining || 0, 0)}</span>
                                            <span className="ml-1">remaining this month</span>
                                            {accessLimit.resetAt && (
                                                <span className="ml-2 opacity-80">
                                                    resets {new Date(accessLimit.resetAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Benefits Preview */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                            {getBenefitsList().slice(0, 4).map((benefit, index) => (
                                <div key={index} className="flex items-start gap-2 min-w-0">
                                    <FiCheckCircle className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${iconClasses[statusConfig.variant as keyof typeof iconClasses]} flex-shrink-0 mt-0.5`} />
                                    <span className={`text-xs sm:text-sm ${textClasses[statusConfig.variant as keyof typeof textClasses]} opacity-80 leading-tight`}>
                                        {benefit}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* CTA Button */}
                <div className="flex-shrink-0 w-full sm:w-auto">
                    <Link
                        to="/subscription"
                        onClick={onVerificationClick}
                        className={`w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 ${buttonClasses[statusConfig.variant as keyof typeof buttonClasses]} text-white text-sm font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2`}
                    >
                        <FiTrendingUp className="h-4 w-4 mr-2 flex-shrink-0" />
                        <span>Manage Plan</span>
                        <FiArrowRight className="ml-2 h-4 w-4 flex-shrink-0 hidden sm:inline" />
                    </Link>
                </div>
            </div>

            {/* Social Proof */}
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-white/20 dark:border-gray-700/50">
                <div className="flex flex-col xs:flex-row xs:items-center xs:justify-between gap-2 xs:gap-4 text-xs sm:text-sm">
                    <div className="flex flex-col xs:flex-row xs:items-center gap-2 xs:gap-4">
                        <div className="flex items-center gap-1">
                            <FiUsers className={`h-3 w-3 flex-shrink-0 ${iconClasses[statusConfig.variant as keyof typeof iconClasses]}`} />
                            <span className={`${textClasses[statusConfig.variant as keyof typeof textClasses]} opacity-60 truncate`}>
                                {platformStats?.data?.verifiedUsers ?
                                    `${platformStats.data.verifiedUsers.toLocaleString()}+ verified users` :
                                    '0 verified users'}
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <FiStar className={`h-3 w-3 flex-shrink-0 ${iconClasses[statusConfig.variant as keyof typeof iconClasses]}`} />
                            <span className={`${textClasses[statusConfig.variant as keyof typeof textClasses]} opacity-60 truncate`}>
                                {platformStats?.data?.averageTrustRating ?
                                    `${platformStats.data.averageTrustRating}/5 trust rating` :
                                    '0/5 trust rating'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        <FiTrendingUp className={`h-3 w-3 flex-shrink-0 ${iconClasses[statusConfig.variant as keyof typeof iconClasses]}`} />
                        <span className={`${textClasses[statusConfig.variant as keyof typeof textClasses]} opacity-60 whitespace-nowrap`}>
                            3x more opportunities
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerificationBanner;